<script setup>
import { computed, ref, watch } from 'vue'
import { useForm } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import { usePublicMoney } from '@/composables/usePublicMoney'
import { usePriceQuote } from '@/composables/usePriceQuote'
import PaymentBadges from '@/Components/Public/PaymentBadges.vue'
import DateField from '@/Components/Public/DateField.vue'
import TurnstileWidget from '@/Components/Public/TurnstileWidget.vue'

// "Book & pay online" checkout card — sits alongside the existing plan
// card / BookingRequestForm on each public Show page. For vehicle/tour/
// property the parent page owns dates/party state AND the usePriceQuote()
// fetch (Task 4); this component only takes the resulting `quote`/
// `quoteLoading` as props so the total shown here is ALWAYS the same live
// server quote already on the page — never a second fetch, never
// re-derived client-side math.
//
// Transfer/Package are different: there is no separate "estimate helper"
// elsewhere on the page needing the same date/class state (a transfer's
// price is a flat per-class number, a package's is a flat listing price —
// neither depends on a date range or party size), so this card owns ALL of
// their operational fields itself (direction, class, pickup date+time,
// flight, hotel, pax/luggage) via its own local form state, and fetches its
// OWN live quote via the same usePriceQuote() composable other cards use —
// re-quoted whenever the selected class changes (transfer) or once on
// mount (package, whose price never varies).
//
// A per_session Experience (Tour with pricing_per_session?, `perSession`
// prop true) is a third case: the parent page still owns the shared
// start_on date (passed through `details.start_on`, same as a per_person
// tour), but there is no adult/child party picker for it to share — so this
// card owns its own quantity stepper AND fetches its own live quote off
// {start_on, quantity}, re-quoting whenever either changes (see `ownQuote`).
//
// Whichever path computed it, the actual charge is recomputed AGAIN
// server-side on submit (Public::BookingsController) regardless of what's
// displayed here; nothing the client sends is ever trusted.
const props = defineProps({
  bookableType:      { type: String, required: true },   // 'vehicle' | 'tour' | 'property' | 'transfer' | 'package' | 'charter'
  bookableId:        { type: [ Number, String ], required: true },
  details:           { type: Object, default: () => ({}) },    // vehicle/tour/property: { start_on, end_on?, adults?, children? }; charter: + charter_format
  disabled:          { type: Boolean, default: false },   // parent gates on missing dates, like BookingRequestForm
  busy:              { type: Boolean, default: false },   // the chosen date is taken on the partner calendar — a different hint than "pick a date"
  showDelivery:      { type: Boolean, default: false },   // vehicle only
  showDepositToggle: { type: Boolean, default: false },   // vehicle: whenever a deposit is set; tour (Experience): only when the vendor opted into deposit_online_allowed
  depositAmount:     { type: String, default: '' },       // pre-formatted (e.g. "฿2,000.00") — vehicle or tour (Experience)
  quote:             { type: Object, default: null },     // vehicle/tour(per_person)/property: usePriceQuote's `quote.value`, reused as-is
  quoteLoading:      { type: Boolean, default: false },
  // Experience (tour) per_session variant — a Tour priced flat per booked
  // session/spot (e.g. a 30-minute jetski slot) rather than per adult/child.
  // Unlike vehicle/property/per_person-tour, this card owns its OWN
  // quantity stepper (there's no other party-size widget on the page for
  // it to share) and, like transfer/package below, fetches its own live
  // quote for it — see `ownQuote`.
  perSession:             { type: Boolean, default: false },
  sessionDurationMinutes: { type: [ Number, String ], default: null },
  // Transfer variant — read-only route/class data the parent already has
  // via the show-page props (never fetched twice).
  fromPoint:         { type: String,  default: '' },
  toPoint:           { type: String,  default: '' },
  fromPointLabel:    { type: String,  default: '' },
  toPointLabel:      { type: String,  default: '' },
  bidirectional:     { type: Boolean, default: false },
  classOptions:      { type: Array,   default: () => [] }, // [{ vehicle_class, seats, luggage, price_cents, display_price }]
  zones:             { type: Array,   default: () => [] }  // Transfer::ZONES — determines whether hotel_name is required
})

const { t } = useI18n()
const { formatMoney } = usePublicMoney()
const isTransfer = computed(() => props.bookableType === 'transfer')
const isPackage  = computed(() => props.bookableType === 'package')
const isTransferLike = computed(() => isTransfer.value || isPackage.value)
const isPerSessionTour = computed(() => props.bookableType === 'tour' && props.perSession)
// Every variant that owns its own live PriceQuote fetch (see the schedule
// watchers below) rather than reusing the parent page's `quote`/`quoteLoading` props.
const ownQuote = computed(() => isTransferLike.value || isPerSessionTour.value)

const form = useForm({
  name: '', email: '', phone: '', delivery_address: '', deposit_online: false, website: '', turnstile_token: '',
  // Transfer/Package only:
  pickup_at: '', flight_number: '', hotel_name: '', pax_count: 1, luggage_count: 0,
  transfer_class: '', direction_from: '', direction_to: '',
  // per_session tour (Experience) only:
  quantity: 1
})

const turnstile = ref(null)

const base = () => (window.location.pathname.startsWith('/_p') ? '/_p' : '')

// --- Transfer: direction toggle -------------------------------------------
const reversed = ref(false)
watch(reversed, () => {
  form.direction_from = reversed.value ? props.toPoint : props.fromPoint
  form.direction_to   = reversed.value ? props.fromPoint : props.toPoint
}, { immediate: true })

const showFlightField = computed(() => form.direction_from === 'airport' || form.direction_to === 'airport')
const showHotelField  = computed(() => props.zones.includes(form.direction_from) || props.zones.includes(form.direction_to))

// --- Transfer: class picker + pax/luggage steppers ------------------------
if (isTransfer.value && props.classOptions.length) {
  form.transfer_class = props.classOptions[0].vehicle_class
}
const selectedOption = computed(() => props.classOptions.find(o => o.vehicle_class === form.transfer_class) || null)
watch(selectedOption, (opt) => {
  if (!opt) return
  if (form.pax_count > opt.seats) form.pax_count = opt.seats
  if (form.luggage_count > opt.luggage) form.luggage_count = opt.luggage
}, { immediate: true })

function bumpPax(delta) {
  const max = selectedOption.value?.seats || 1
  form.pax_count = Math.min(max, Math.max(1, form.pax_count + delta))
}
function bumpLuggage(delta) {
  const max = selectedOption.value?.luggage || 0
  form.luggage_count = Math.min(max, Math.max(0, form.luggage_count + delta))
}

// --- Transfer/Package: pickup date+time (min = now + 6h) ------------------
// A transfer/package pickup is a PHUKET wall-clock instant, not a browser-
// local one — the driver dispatches off the actual Asia/Bangkok time, and
// the business operates entirely in Phuket regardless of which timezone the
// customer's browser happens to be in (a Moscow customer typing "14:30"
// means 14:30 IN PHUKET, not 14:30 Moscow re-expressed as UTC — that would
// silently dispatch the driver 4 hours off from what the customer picked).
// The Rails app's own Time.zone is UTC (unconfigured — see config/
// application.rb), so this offset can't be delegated to the server either:
// the client must send an EXPLICIT +07:00 offset (see submit() below) and
// must present/compute this field's `min` in that same Phuket frame, not
// the browser's. Asia/Bangkok has no DST, so a fixed +07:00 is safe
// year-round (unlike a generic toLocalInputValue-with-getTimezoneOffset()
// helper, which would reflect the BROWSER's zone).
const PHUKET_UTC_OFFSET_MINUTES = 7 * 60
function toPhuketWallClock(d) {
  return new Date(d.getTime() + PHUKET_UTC_OFFSET_MINUTES * 60000).toISOString().slice(0, 16)
}
const minPickup = computed(() => toPhuketWallClock(new Date(Date.now() + 6 * 60 * 60 * 1000)))

// --- Transfer/Package/per_session-tour: this card's own live quote --------
const { quote: liveQuote, loading: liveLoading, schedule: scheduleQuote } = usePriceQuote(props.bookableType, props.bookableId)
if (isPackage.value) scheduleQuote({}) // flat price — fetch once, no params needed
watch(() => form.transfer_class, (v) => {
  if (isTransfer.value) scheduleQuote(v ? { vehicle_class: v } : null)
}, { immediate: true })
// per_session tour (Experience): re-quoted off the parent-owned date
// (`details.start_on` — shared with the page's own party/date picker) and
// this card's own quantity stepper. Bumping quantity re-fetches so the
// total shown here always reflects session_price × quantity, exactly what
// the server will freeze on submit.
watch([ () => props.details?.start_on, () => form.quantity ], ([ startOn, qty ]) => {
  if (isPerSessionTour.value) scheduleQuote(startOn ? { start_on: startOn, quantity: qty } : null)
}, { immediate: true })

const effectiveQuote   = computed(() => (ownQuote.value ? liveQuote.value : props.quote))
const effectiveLoading = computed(() => (ownQuote.value ? liveLoading.value : props.quoteLoading))

const totalDisplay = computed(() => (effectiveQuote.value ? formatMoney(effectiveQuote.value.total_cents) : null))

const transferReady = computed(() => {
  if (isTransfer.value) {
    return !!form.pickup_at && !!form.transfer_class && (!showHotelField.value || !!form.hotel_name)
  }
  if (isPackage.value) {
    return !!form.pickup_at && !!form.hotel_name
  }
  return true
})
const canSubmit = computed(() => !props.disabled && transferReady.value && !effectiveLoading.value && !!effectiveQuote.value && !form.processing)

function bumpQuantity(delta) {
  form.quantity = Math.max(1, form.quantity + delta)
}

// `d.pickup_at` is a bare datetime-local string ("2026-08-01T14:30", no
// seconds/offset) that this card's own inputs/`min` treat as PHUKET wall-
// clock (see the comment above `minPickup`). Appending the explicit
// "+07:00" offset — rather than round-tripping through `new Date(...)`,
// which would parse the bare string using the BROWSER's local timezone —
// turns it into an unambiguous ISO8601 instant that Rails'
// `Time.zone.parse` (app tz is UTC) resolves correctly regardless of where
// the customer or the server happen to be.
function phuketPickupAt(value) {
  return value ? `${value}:00+07:00` : null
}

function submit() {
  form.transform(d => ({
    booking: {
      bookable_type:    props.bookableType,
      bookable_id:      props.bookableId,
      ...(isTransfer.value ? {
        pickup_at:      phuketPickupAt(d.pickup_at),
        flight_number:  d.flight_number,
        hotel_name:     d.hotel_name,
        pax_count:      d.pax_count,
        luggage_count:  d.luggage_count,
        transfer_class: d.transfer_class,
        direction_from: d.direction_from,
        direction_to:   d.direction_to
      } : isPackage.value ? {
        pickup_at:  phuketPickupAt(d.pickup_at),
        hotel_name: d.hotel_name
      } : isPerSessionTour.value ? { ...props.details, quantity: d.quantity } : props.details),
      name:             d.name,
      email:            d.email,
      phone:            d.phone,
      delivery_address: d.delivery_address,
      deposit_online:   d.deposit_online
    },
    website: d.website,
    turnstile_token: d.turnstile_token
  })).post(`${base()}/bookings`, {
    preserveScroll: true,
    onFinish: () => turnstile.value?.reset()
  })
}
</script>

<template>
  <div class="checkout-card">
    <h2 class="section-title">{{ t('public.checkout.title') }}</h2>

    <p v-if="totalDisplay" class="checkout-total">
      {{ t('public.pay.amount_label') }}
      <span class="fw-value">{{ totalDisplay }}</span>
    </p>

    <form class="checkout-form" @submit.prevent="submit">
      <input
        v-model="form.website"
        type="text"
        name="website"
        tabindex="-1"
        autocomplete="off"
        class="checkout-honeypot"
        aria-hidden="true"
      >

      <template v-if="isTransfer">
        <div v-if="bidirectional" class="checkout-direction" role="radiogroup" :aria-label="t('public.checkout.transfer.direction_label')">
          <button
            type="button"
            :class="['checkout-direction__btn', !reversed && 'is-active']"
            :aria-pressed="!reversed"
            @click="reversed = false"
          >{{ fromPointLabel }} → {{ toPointLabel }}</button>
          <button
            type="button"
            :class="['checkout-direction__btn', reversed && 'is-active']"
            :aria-pressed="reversed"
            @click="reversed = true"
          >{{ toPointLabel }} → {{ fromPointLabel }}</button>
        </div>

        <div class="checkout-field">
          <span class="checkout-label">{{ t('public.checkout.transfer.class_label') }}</span>
          <div class="checkout-classes" role="radiogroup" :aria-label="t('public.checkout.transfer.class_label')">
            <label
              v-for="o in classOptions"
              :key="o.vehicle_class"
              :class="['checkout-class-row', form.transfer_class === o.vehicle_class && 'is-active']"
            >
              <input v-model="form.transfer_class" type="radio" name="checkout_transfer_class" :value="o.vehicle_class">
              <span class="checkout-class-row__name">{{ t(`public.transfers.vehicle_classes.${o.vehicle_class}`) }}</span>
              <span class="checkout-class-row__meta">{{ o.seats }} · {{ o.luggage }}</span>
              <span class="checkout-class-row__price fw-value">{{ o.display_price }}</span>
            </label>
          </div>
        </div>

        <label class="checkout-field">
          <span class="checkout-label">{{ t('public.checkout.transfer.pickup_label') }}</span>
          <DateField v-model="form.pickup_at" type="datetime-local" class="checkout-input" :min="minPickup" required :placeholder="t('public.pick_datetime')" />
        </label>

        <label v-if="showFlightField" class="checkout-field">
          <span class="checkout-label">{{ t('public.checkout.transfer.flight_label') }}</span>
          <input
            v-model="form.flight_number"
            type="text"
            class="checkout-input"
            maxlength="20"
            :placeholder="t('public.checkout.transfer.flight_placeholder')"
          >
        </label>

        <label v-if="showHotelField" class="checkout-field">
          <span class="checkout-label">{{ t('public.checkout.transfer.hotel_label') }}</span>
          <input
            v-model="form.hotel_name"
            type="text"
            class="checkout-input"
            maxlength="120"
            required
            :placeholder="t('public.checkout.transfer.hotel_placeholder')"
          >
        </label>

        <div class="checkout-grid">
          <div class="checkout-field">
            <span class="checkout-label">{{ t('public.checkout.transfer.pax_label') }}</span>
            <span class="checkout-stepper">
              <button type="button" class="checkout-stepper__btn" :aria-label="t('public.checkout.transfer.pax_label')" @click="bumpPax(-1)">−</button>
              <span class="checkout-stepper__n fw-value">{{ form.pax_count }}</span>
              <button type="button" class="checkout-stepper__btn" :aria-label="t('public.checkout.transfer.pax_label')" @click="bumpPax(1)">+</button>
            </span>
          </div>
          <div class="checkout-field">
            <span class="checkout-label">{{ t('public.checkout.transfer.luggage_label') }}</span>
            <span class="checkout-stepper">
              <button type="button" class="checkout-stepper__btn" :aria-label="t('public.checkout.transfer.luggage_label')" @click="bumpLuggage(-1)">−</button>
              <span class="checkout-stepper__n fw-value">{{ form.luggage_count }}</span>
              <button type="button" class="checkout-stepper__btn" :aria-label="t('public.checkout.transfer.luggage_label')" @click="bumpLuggage(1)">+</button>
            </span>
          </div>
        </div>
      </template>

      <template v-else-if="isPackage">
        <label class="checkout-field">
          <span class="checkout-label">{{ t('public.checkout.package.pickup_label') }}</span>
          <DateField v-model="form.pickup_at" type="datetime-local" class="checkout-input" :min="minPickup" required :placeholder="t('public.pick_datetime')" />
        </label>

        <label class="checkout-field">
          <span class="checkout-label">{{ t('public.checkout.package.hotel_label') }}</span>
          <input
            v-model="form.hotel_name"
            type="text"
            class="checkout-input"
            maxlength="120"
            required
            :placeholder="t('public.checkout.package.hotel_placeholder')"
          >
        </label>
      </template>

      <template v-else-if="isPerSessionTour">
        <div class="checkout-field">
          <span class="checkout-label">{{ t('public.checkout.quantity_label') }}</span>
          <span class="checkout-stepper">
            <button type="button" class="checkout-stepper__btn" :aria-label="t('public.checkout.quantity_label')" @click="bumpQuantity(-1)">−</button>
            <span class="checkout-stepper__n fw-value">{{ form.quantity }}</span>
            <button type="button" class="checkout-stepper__btn" :aria-label="t('public.checkout.quantity_label')" @click="bumpQuantity(1)">+</button>
          </span>
        </div>
        <p v-if="sessionDurationMinutes" class="checkout-hint">{{ t('public.checkout.session_duration', { minutes: sessionDurationMinutes }) }}</p>
      </template>

      <label class="checkout-field">
        <span class="checkout-label">{{ t('public.checkout.name') }}</span>
        <input v-model="form.name" type="text" class="checkout-input" required maxlength="80" autocomplete="name">
      </label>

      <div class="checkout-grid">
        <label class="checkout-field">
          <span class="checkout-label">{{ t('public.checkout.email') }}</span>
          <input v-model="form.email" type="email" class="checkout-input" autocomplete="email">
        </label>
        <label class="checkout-field">
          <span class="checkout-label">{{ t('public.checkout.phone') }}</span>
          <input v-model="form.phone" type="tel" class="checkout-input" autocomplete="tel">
        </label>
      </div>
      <p class="checkout-hint">{{ t('public.checkout.contact_hint') }}</p>

      <label v-if="showDelivery" class="checkout-field">
        <span class="checkout-label">{{ t('public.checkout.delivery_address') }}</span>
        <input
          v-model="form.delivery_address"
          type="text"
          class="checkout-input"
          maxlength="200"
          :placeholder="t('public.checkout.delivery_placeholder')"
          autocomplete="street-address"
        >
      </label>

      <label v-if="showDepositToggle" class="checkout-toggle">
        <input v-model="form.deposit_online" type="checkbox">
        <span>{{ t('public.checkout.deposit_toggle', { amount: depositAmount }) }}</span>
      </label>
      <!-- Deposit-READY notice: shown whenever a deposit amount is set at
           all, independent of showDepositToggle — a vehicle's deposit is
           always toggle-eligible so the two happen to coincide there, but a
           tour (Experience) can have a deposit configured WITHOUT the vendor
           having opted into online collection (deposit_online_allowed:
           false), and should still tell the customer to bring it. -->
      <p v-if="!form.deposit_online && depositAmount" class="checkout-deposit-notice">
        {{ t('public.checkout.deposit_notice', { amount: depositAmount }) }}
      </p>

      <!-- div, not p: PaymentBadges renders a block <div>, and a div inside a
           <p> makes the browser close the paragraph early, so the server HTML
           and the client vdom disagree at hydration (found on the SSR launch). -->
      <div class="checkout-methods">
        <PaymentBadges />
        <span>{{ t('public.checkout.methods_hint') }}</span>
      </div>
      <p class="checkout-hold-note">{{ t(isTransferLike ? 'public.checkout.price_hold_note' : 'public.checkout.hold_note') }}</p>

      <TurnstileWidget ref="turnstile" v-model="form.turnstile_token" />

      <button type="submit" class="checkout-submit" :disabled="!canSubmit">
        {{ form.processing ? t('public.checkout.redirecting') : (totalDisplay ? t('public.checkout.pay_now', { amount: totalDisplay }) : t('public.checkout.title')) }}
      </button>
      <p v-if="disabled" class="checkout-hint">{{ busy ? t('public.checkout.date_busy') : t('public.checkout.pick_date') }}</p>
    </form>
  </div>
</template>

<style scoped>
.checkout-card {
  margin-top: 1.25rem; padding: 1.25rem;
  background: var(--surface); border: 1px solid var(--line);
  border-radius: var(--radius-table, 0.75rem);
}
.checkout-total { margin: 0 0 0.9rem; font-size: 1.05rem; color: var(--ink); display: flex; justify-content: space-between; align-items: baseline; gap: 0.5rem; }
.checkout-form { display: grid; gap: 0.6rem; }
.checkout-honeypot { position: absolute; left: -9999px; }
.checkout-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.6rem; }
.checkout-field { display: block; }
.checkout-label { display: block; font-size: 0.8rem; font-weight: 600; color: var(--inkSoft); margin-bottom: 0.2rem; }
.checkout-input {
  width: 100%; box-sizing: border-box; padding: 0.5rem 0.7rem; font-size: 0.95rem; color: var(--ink);
  background: var(--bg); border: 1px solid var(--line); border-radius: var(--radius-input, 0.5rem);
  color-scheme: light;
}
.checkout-hint { margin: 0; font-size: 0.78rem; color: var(--muted); }
.checkout-toggle { display: flex; align-items: center; gap: 0.5rem; font-size: 0.9rem; color: var(--inkSoft); margin-top: 0.2rem; }
.checkout-deposit-notice { margin: 0; font-size: 0.8rem; color: var(--warn); }
.checkout-methods { display: flex; align-items: center; gap: 0.6rem; color: var(--inkSoft); font-size: 0.85rem; margin: 0.3rem 0 0; }
.checkout-hold-note { margin: 0; font-size: 0.78rem; color: var(--muted); }
.checkout-submit { width: 100%; display: inline-flex; align-items: center; justify-content: center; padding: 0.95rem 1.6rem; border-radius: 999px; cursor: pointer; appearance: none; font-family: var(--font-body, var(--body)); font-size: 0.78rem; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; line-height: 1; background: var(--night, var(--ink)); color: var(--foam, #ECF3F6); border: 1px solid var(--night, var(--ink)); box-shadow: 0 16px 34px -20px color-mix(in oklch, var(--ink) 60%, transparent), inset 0 0 0 1px color-mix(in oklch, var(--gold, #D8B46B) 40%, transparent); transition: transform 160ms ease, background 160ms ease; }
.checkout-submit:hover:not(:disabled) { transform: translateY(-1px); background: var(--night2, var(--ink)); border-color: var(--night2, var(--ink)); }
.checkout-submit:disabled { opacity: 0.5; cursor: not-allowed; }

.checkout-direction { display: flex; flex-direction: column; gap: 0.4rem; }
.checkout-direction__btn {
  padding: 0.55rem 0.8rem; border-radius: var(--radius-input, 0.5rem);
  border: 1px solid var(--line); background: var(--bg); color: var(--ink);
  font: inherit; font-size: 0.9rem; text-align: left; cursor: pointer;
  transition: border-color 150ms ease, color 150ms ease;
}
.checkout-direction__btn.is-active { border-color: var(--accent); color: var(--accent); font-weight: 700; }

.checkout-classes { display: grid; gap: 0.5rem; }
.checkout-class-row {
  display: flex; align-items: center; gap: 0.6rem; padding: 0.55rem 0.7rem;
  border: 1px solid var(--line); border-radius: var(--radius-input, 0.5rem);
  background: var(--bg); cursor: pointer; transition: border-color 150ms ease;
}
.checkout-class-row.is-active { border-color: var(--accent); }
.checkout-class-row input[type="radio"] { flex: none; }
.checkout-class-row__name { flex: 1; font-weight: 600; color: var(--ink); font-size: 0.9rem; }
.checkout-class-row__meta { color: var(--muted); font-size: 0.8rem; }
.checkout-class-row__price { color: var(--ink); font-size: 0.9rem; }

.checkout-stepper { display: inline-flex; align-items: center; gap: 0.5rem; }
.checkout-stepper__btn {
  width: 1.9rem; height: 1.9rem; border-radius: 999px;
  border: 1px solid var(--line); background: var(--surface); color: var(--ink);
  font-size: 1rem; line-height: 1; cursor: pointer; display: grid; place-items: center;
}
.checkout-stepper__btn:hover { border-color: var(--accent); color: var(--accent); }
.checkout-stepper__n { min-width: 1.4rem; text-align: center; }
</style>
